import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabase } from "@/integrations/supabase/client";

export type CrawlerLinkPage = "home" | "pakistan" | "india" | "footer";
export type CrawlerLink = {
  id: string;
  page: CrawlerLinkPage;
  url: string;
  label: string;
  sort_order: number;
  active: boolean;
};

const PageSchema = z.enum(["home", "pakistan", "india", "footer"]);
const UrlSchema = z.string().trim().min(1).max(400);
const LabelSchema = z.string().trim().min(1).max(140);

// PUBLIC: list active links for a given page (used by SSR render of the strip).
export const listCrawlerLinks = createServerFn({ method: "GET" })
  .inputValidator((d) => PageSchema.parse((d as { page: CrawlerLinkPage }).page))
  .handler(async ({ data: page }): Promise<CrawlerLink[]> => {
    const { data, error } = await supabase
      .from("crawler_link_slots")
      .select("id,page,url,label,sort_order,active")
      .eq("page", page)
      .eq("active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) {
      console.error("[crawler-links] list error", error);
      return [];
    }
    return (data ?? []) as CrawlerLink[];
  });

// ADMIN: list all (incl. inactive) for a page.
export const adminListCrawlerLinks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => PageSchema.parse((d as { page: CrawlerLinkPage }).page))
  .handler(async ({ data: page, context }): Promise<CrawlerLink[]> => {
    const { userId } = context as { userId: string };
    const { data: roleRow } = await (context as { supabase: typeof supabase }).supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (!roleRow) throw new Response("Forbidden", { status: 403 });

    const { data, error } = await (context as { supabase: typeof supabase }).supabase
      .from("crawler_link_slots")
      .select("id,page,url,label,sort_order,active")
      .eq("page", page)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Response(error.message, { status: 500 });
    return (data ?? []) as CrawlerLink[];
  });

// ADMIN: create a link at the bottom of the page's list.
export const adminCreateCrawlerLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({ page: PageSchema, url: UrlSchema, label: LabelSchema })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { userId, supabase: sb } = context as { userId: string; supabase: typeof supabase };
    const { data: roleRow } = await sb
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Response("Forbidden", { status: 403 });

    // Next sort_order = max + 1
    const { data: maxRow } = await sb
      .from("crawler_link_slots").select("sort_order")
      .eq("page", data.page).order("sort_order", { ascending: false }).limit(1).maybeSingle();
    const nextOrder = (maxRow?.sort_order ?? 0) + 1;

    const { data: inserted, error } = await sb
      .from("crawler_link_slots")
      .insert({ page: data.page, url: data.url, label: data.label, sort_order: nextOrder, active: true })
      .select("id,page,url,label,sort_order,active")
      .single();
    if (error) throw new Response(error.message, { status: 500 });
    return inserted as CrawlerLink;
  });

// ADMIN: toggle active flag / update url or label.
export const adminUpdateCrawlerLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        id: z.string().uuid(),
        url: UrlSchema.optional(),
        label: LabelSchema.optional(),
        active: z.boolean().optional(),
        sort_order: z.number().int().min(0).max(9999).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { userId, supabase: sb } = context as { userId: string; supabase: typeof supabase };
    const { data: roleRow } = await sb
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Response("Forbidden", { status: 403 });

    const patch: {
      url?: string;
      label?: string;
      active?: boolean;
      sort_order?: number;
    } = {};
    if (data.url !== undefined) patch.url = data.url;
    if (data.label !== undefined) patch.label = data.label;
    if (data.active !== undefined) patch.active = data.active;
    if (data.sort_order !== undefined) patch.sort_order = data.sort_order;
    if (Object.keys(patch).length === 0) return { ok: true };

    const { error } = await sb.from("crawler_link_slots").update(patch).eq("id", data.id);
    if (error) throw new Response(error.message, { status: 500 });
    return { ok: true };
  });

// ADMIN: delete a link permanently.
export const adminDeleteCrawlerLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { userId, supabase: sb } = context as { userId: string; supabase: typeof supabase };
    const { data: roleRow } = await sb
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Response("Forbidden", { status: 403 });
    const { error } = await sb.from("crawler_link_slots").delete().eq("id", data.id);
    if (error) throw new Response(error.message, { status: 500 });
    return { ok: true };
  });